import { useState } from 'react';
import { BellPlus, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import CryptoIcon from './CryptoIcon';
import { priceAlertsAPI } from '../services/api';
import { getCommodity } from '../utils/commodities';

export const PRICE_ALERTS_CHANGED = 'price-alerts-changed';

const ConditionButton = ({ value, current, onClick, Icon, label }) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    data-testid={`price-alert-condition-${value}`}
    className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm border transition-colors ${
      current === value
        ? value === 'above' ? 'bg-emerald-500/15 border-emerald-500 text-emerald-300' : 'bg-red-500/15 border-red-500 text-red-300'
        : 'bg-gray-800/60 border-gray-700 text-gray-400 hover:text-white'
    }`}
  >
    <Icon className="h-4 w-4" /> {label}
  </button>
);

export const PriceAlertButton = ({ crypto }) => {
  const metal = getCommodity(crypto?.symbol);
  const [open, setOpen] = useState(false);
  const [condition, setCondition] = useState('above');
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);

  if (!metal) return null;
  const price = crypto.current_price || 0;

  const openDialog = () => {
    setCondition('above');
    setTarget(price ? (price * 1.01).toFixed(2) : '');
    setOpen(true);
  };

  const submit = async () => {
    const value = parseFloat(target);
    if (!value || value <= 0) return toast.error('Enter a valid target price');
    setSaving(true);
    try {
      await priceAlertsAPI.create({ symbol: metal.symbol, condition, target_price: value });
      toast.success(`Alert set: ${metal.symbol}/USD ${condition} $${value.toLocaleString()}`);
      window.dispatchEvent(new Event(PRICE_ALERTS_CHANGED));
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create alert');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        data-testid="price-alert-open-btn"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 transition-colors"
      >
        <BellPlus className="h-3.5 w-3.5" /> Price Alert
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0d1424] border-purple-500/30 text-white sm:max-w-md" data-testid="price-alert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <CryptoIcon symbol={metal.symbol} size={32} /> {metal.name} Alert
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Current price ${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}. We&apos;ll notify you once when the target is reached.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <ConditionButton value="above" current={condition} onClick={setCondition} Icon={ArrowUpRight} label="Goes above" />
            <ConditionButton value="below" current={condition} onClick={setCondition} Icon={ArrowDownRight} label="Goes below" />
          </div>
          <label className="text-sm text-gray-300 font-medium">Target price (USD)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="price-alert-target-input"
            className="w-full px-4 py-3 bg-gray-800 rounded-lg text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="price-alert-submit-btn"
            className="w-full py-3 rounded-xl font-bold bg-gradient-to-r from-purple-600 to-violet-500 hover:from-purple-500 hover:to-violet-400 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Create Alert'}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PriceAlertButton;
